import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  LinearProgress,
  Alert,
  CssBaseline,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import axios from "axios";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { message } from "../Constants/message.js";
import CreditsBanner from "../Components/CreditsNudge.js";
import UpgradeModal from "../Components/UpgradeModal.js";
import CommentSection from "../Components/CommentSection.js";
import {
  ContentBox,
  StyledButton,
  TitleText,
  SubtitleText,
  PageContainer,
} from "./Default.js";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import YouTubeDescriptionExtractorContent from "../Contents/YouTubeDescriptionExtractorContent.js";
const convertLinksToAnchors = (text) => {
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;

  return text.replace(urlRegex, (url) => {
    const href = url.startsWith("http") ? url : `https://${url}`;
    return `<a href="${href}" target="_blank" rel="noopener noreferrer" style="color:#1a73e8; text-decoration:underline;">${url}</a>`;
  });
};
// ✅ Inline Component to Preview Video Metadata
const VideoMetadataPreview = ({
  title,
  channelTitle,
  uploadDate,
  description,
  tags,
}) => {
  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <Box
      mt={2}
      p={2}
      border="1px solid #ccc"
      borderRadius="8px"
      bgcolor="#f9f9f9"
    >
      {title && (
        <>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="subtitle1" fontWeight="bold">
              📽 {title}
            </Typography>
            <Tooltip title="Copy Title">
              <IconButton onClick={() => handleCopy(title)} size="small">
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Divider sx={{ my: 2 }} />
        </>
      )}

      {(channelTitle || uploadDate) && (
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2" color="text.secondary">
              Uploaded by <b>{channelTitle}</b> on {uploadDate}
            </Typography>
            <Tooltip title="Copy Channel Info">
              <IconButton
                onClick={() =>
                  handleCopy(`Uploaded by ${channelTitle} on ${uploadDate}`)
                }
                size="small"
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Divider sx={{ my: 2 }} />
        </>
      )}

      {description && (
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="subtitle2" fontWeight="bold">
              🔎 Description:
            </Typography>
            <Tooltip title="Copy Description">
              <IconButton onClick={() => handleCopy(description)} size="small">
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography
            variant="body2"
            component="div"
            whiteSpace="pre-wrap"
            sx={{
              fontFamily: "monospace",
              mt: 1,
              lineHeight: 1.7,
              wordBreak: "break-word",
            }}
            dangerouslySetInnerHTML={{
              __html: convertLinksToAnchors(description),
            }}
          />
        </>
      )}
    </Box>
  );
};

function YouTubeDescriptionExtractor({ user, login, isLoggedIn }) {
  const navigate = useNavigate();
  const [videoUrl, setVideoUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Metadata
  const [title, setTitle] = useState("");
  const [channelTitle, setChannelTitle] = useState("");
  const [uploadDate, setUploadDate] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState([]);

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [credits, setCredits] = useState({
    remaining: 0,
    availed: 0,
    total: 0,
    loading: true,
  });

  useEffect(() => {
    const primaryInputText = localStorage.getItem("primaryInputText");
    if (primaryInputText) {
      setVideoUrl(primaryInputText);
      localStorage.removeItem("primaryInputText");
    }
  }, []);

 const validateUrl = (url) => {
    const pattern =
      /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})([&?=\w-]*)?$/i;

    const isValid = pattern.test(url.trim());

    if (!isValid) {
      setError(
        "❌ Invalid YouTube URL. Use formats like https://www.youtube.com/watch?v=VIDEO_ID, /shorts/VIDEO_ID, or youtu.be/VIDEO_ID"
      );
    }

    return isValid;
  };


  const handleExtract = async () => {
    if (!isLoggedIn) {
      setError(message.loginPrompt);
      setTimeout(() => login(), 1500);
      return;
    }

    setError("");
    setSuccess("");
    setDescription("");
    setTitle("");
    setTags([]);
    setChannelTitle("");
    setUploadDate("");

    if (!validateUrl(videoUrl)) return;

    if (credits.remaining <= 0) {
      setError("You don’t have enough credits to extract video description.");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/youtube/description-extractor`,
        { youtubeVideoUrl: videoUrl },
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        }
      );

      const data = response.data;
      setTitle(data.title || "");
      setChannelTitle(data.channelTitle || "");
      setUploadDate(data.uploadDate || "");
      setDescription(data.description || "");
      setTags(data.tags || []);
      setSuccess(data.successMessage);

      const creditsResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/credit`
      );
      setCredits({
        remaining: creditsResponse.data.remaining_credits || 0,
        availed: creditsResponse.data.availed_credits || 0,
        total: creditsResponse.data.total_credits || 0,
        loading: false,
      });

      if (
        [0, 2, 4, 6].includes(creditsResponse.data.availed_credits) &&
        user.account_type.toLowerCase() === "free"
      ) {
        setTimeout(() => {
          setShowUpgradeModal(true);
        }, 3000);
      }
    } catch (err) {
      setError(
        err.response?.data?.errorMessage ||
          "Failed to extract description. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>YouTube Description Extractor | Get YouTube Video and shorts Metadata & Tags</title>
        <meta
          name="description"
          content="Extract YouTube video descriptions, tags, title, and channel details instantly. Paste a video URL and generate optimized metadata in seconds."
        />
        <meta
          name="keywords"
          content="YouTube description extractor, generate YouTube description, video tags extractor, YouTube metadata tool, extract YouTube video info"
        />
        <meta property="og:title" content="YouTube Description Extractor" />
        <meta
          property="og:description"
          content="Paste a video URL and instantly extract video title, description, tags, channel name, and upload date."
        />
        <meta
          property="og:url"
          content="https://myfreshtools.com/youtube-description-generator"
        />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="YouTube Description Extractor" />
        <meta
          name="twitter:description"
          content="Generate and extract YouTube video metadata like title, tags, and descriptions automatically."
        />
        <link
          rel="canonical"
          href="https://myfreshtools.com/youtube-description-generator"
        />
      </Helmet>

      {isLoggedIn && (
        <CreditsBanner credits={credits} setCredits={setCredits} />
      )}
      <CssBaseline />
      <PageContainer sx={{ paddingTop: 3 }}>
        <ContentBox>
          <TitleText variant="h1" align="center" sx={{ fontSize: "2.8rem" }}>
            YouTube Description Extractor | Videos & Shorts
          </TitleText>

          <SubtitleText variant="h2" align="center" sx={{ fontSize: "1.2rem" }}>
            Paste a Video/Shorts URL & Extract YouTube Description in Seconds
          </SubtitleText>

          <Box
            mt={3}
            mb={3}
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "stretch", sm: "flex-end" }}
            gap={2}
          >
            <TextField
              label="Paste Your YouTube Video URL"
              variant="outlined"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=VIDEO_ID"
              size="medium"
              id="youtube-description-url"
              name="primary-textbox"
              sx={{
                flexGrow: 1,
                "& .MuiOutlinedInput-root": {
                  height: "56px",
                },
              }}
              fullWidth
            />
            <StyledButton
              variant="contained"
              color="primary"
              onClick={handleExtract}
              sx={{
                minWidth: "220px", // Increased from 180px to 220px
                width: { xs: "100%", sm: "220px" }, // Full width on mobile, fixed on desktop
                height: "56px",
                whiteSpace: "nowrap",
                alignSelf: { sm: "flex-end" },
              }}
            >
              Extract Description
            </StyledButton>
          </Box>

          <Box display="flex" flexDirection="column" gap={1} mb={2}>
            <Typography variant="body2" color="text.secondary">
              ✅ <b>1 extraction = 1 Credit</b>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              🧠 Generates detailed descriptions using real YouTube video
              metadata.
            </Typography>
          </Box>

          {loading && <LinearProgress sx={{ mb: 2 }} />}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {(title || description) && (
            <VideoMetadataPreview
              title={title}
              channelTitle={channelTitle}
              uploadDate={uploadDate}
              description={description}
              tags={tags}
            />
          )}

          <CommentSection
            toolId="youtube/description-generator"
            user={user}
            login={login}
            isLoggedIn={isLoggedIn}
          />

          <UpgradeModal
            open={showUpgradeModal}
            onClose={() => setShowUpgradeModal(false)}
            onUpgrade={() => {
              navigate("/Pricing");
              setShowUpgradeModal(false);
            }}
            title="🚀 Generate More Descriptions with Pro"
            subTitle="Upgrade now to unlock full description access."
          />
        </ContentBox>
        <YouTubeDescriptionExtractorContent />
      </PageContainer>
    </>
  );
}

export default YouTubeDescriptionExtractor;
